import { computeLegNotional } from './pairPnlSeries';

/**
 * The pair spread, rebuilt minute by minute from OHLC candles.
 *
 * Spread = futures notional ÷ basket value − 1, for ONE contract against a basket
 * whose share counts are fixed at the PREVIOUS session's close (ATC). At that
 * close the basket is worth exactly the futures notional, so the spread starts the
 * session at 0 and every move after is the futures leg moving apart from the
 * basket. Positive ⇒ futures got rich against the basket (the side a short-futures
 * pair wants to open on).
 *
 * Why the previous close and not today's open: the ATC is the one moment both legs
 * print together in a call auction, so the mark is synchronised and free of bid/ask
 * bounce. It is also known before 09:00, so the spread is defined from the first
 * tick instead of waiting for every leg's first trade.
 *
 * Share counts stay UNROUNDED (fractional shares). The line is a market signal, not
 * an order: board-lot rounding would make the basket jump a whole lot whenever a
 * price crossed a boundary, and the spread with it.
 *
 * ONLY the candle's CLOSE is read. Units: stocks thousand VND, futures index points —
 * the same as the live feed, so no conversion.
 *
 * CLOCK — the two legs come from two tables on two different clocks and
 * `/price/chart` does not normalise them: stock candles arrive 25200 s ahead of the
 * instant they describe, futures candles arrive on a true UTC epoch. The hook
 * corrects each on the way in (`clockOffsetSeconds`); everything below assumes true
 * UTC throughout.
 */

/** VN is UTC+7; see the CLOCK note above. */
export const VN_OFFSET_SECONDS = 7 * 3600;

const STOCK_UNIT = 1000;

/** Bare ticker of an exchange-qualified id: "HNXDS:VN30F2609" → "VN30F2609". */
const tickerOf = (symbol: string): string => symbol.split(':').pop() ?? symbol;
const SECONDS_PER_DAY = 24 * 3600;

export interface Candle {
    time: number;
    open: number;
    close: number;
}

/** A bar of the chart: both legs as notional, raw VND. */
export interface NotionalHistoryPoint {
    /** Unix seconds, true UTC — the same clock `usePnlHistory` samples on. */
    time: number;
    futValue: number;
    equityValue: number;
}

/** Each leg's close at the end of the previous session — the spread's zero. */
export interface PreviousCloseMarks {
    future: number | null;
    byStock: Record<string, number | null>;
}

interface BasketStock {
    symbol: string;
    proportion?: number | null;
}

/** VN calendar day an instant falls on, as a day number. */
export const vnDay = (epochSeconds: number): number =>
    Math.floor((epochSeconds + VN_OFFSET_SECONDS) / SECONDS_PER_DAY);

/** Every session present, oldest first. */
const sessionDays = (candles: Candle[]): number[] =>
    Array.from(new Set(candles.map((c) => vnDay(c.time)))).sort((a, b) => a - b);

/** Close of the last candle before `day`; a leg that skipped a session keeps its older close. */
const lastCloseBefore = (candles: Candle[], day: number): number | null => {
    for (let i = candles.length - 1; i >= 0; i -= 1) {
        if (vnDay(candles[i].time) < day) return candles[i].close;
    }
    return null;
};

/**
 * How far a leg's candle times sit from the instant they describe.
 *
 *   stocks  — `quote.matched.datetime`, VN wall time in a column WITHOUT a time
 *             zone, which Prisma serialises as if it were UTC. Arrives 25200 s
 *             ahead of the real instant.
 *   futures — the main DB's `Ohlc`, already a true UTC epoch.
 */
export function clockOffsetSeconds(symbol: string): number {
    const isFutures = symbol.startsWith('HNXDS:') || tickerOf(symbol).startsWith('VN30F');
    return isFutures ? 0 : VN_OFFSET_SECONDS;
}

/** Every leg's close on the last session before `day`. */
export function previousCloseMarks(
    futureCandles: Candle[],
    stockCandles: Record<string, Candle[]>,
    stocks: BasketStock[],
    day: number,
): PreviousCloseMarks {
    const byStock: Record<string, number | null> = {};
    stocks.forEach((stock) => {
        byStock[stock.symbol] = lastCloseBefore(stockCandles[stock.symbol] ?? [], day);
    });
    return { future: lastCloseBefore(futureCandles, day), byStock };
}

/**
 * Shares per ONE contract, unrounded: the basket worth exactly the futures notional
 * at the marks, split by weight. Null when any weighted leg has no mark — a basket
 * missing a symbol is a different basket, not a smaller one.
 */
export function spreadBasket(
    marks: PreviousCloseMarks,
    stocks: BasketStock[],
    multiplier: number,
): Record<string, number> | null {
    if (marks.future == null || !(marks.future > 0)) return null;
    const weighted = stocks.filter((s) => (s.proportion ?? 0) > 0);
    const totalWeight = weighted.reduce((sum, s) => sum + (s.proportion as number), 0);
    if (!weighted.length || !(totalWeight > 0)) return null;

    const notional = marks.future * multiplier;
    const qty: Record<string, number> = {};
    for (const stock of weighted) {
        const mark = marks.byStock[stock.symbol];
        if (mark == null || !(mark > 0)) return null;
        qty[stock.symbol] = (notional * ((stock.proportion as number) / totalWeight)) / (mark * STOCK_UNIT);
    }
    return qty;
}

/**
 * Futures ÷ basket − 1, in percent. Null when a leg has no value.
 */
export function spreadPercent(futValue: number | null, equityValue: number | null): number | null {
    if (futValue == null || equityValue == null || !(equityValue > 0)) return null;
    return (futValue / equityValue - 1) * 100;
}

/**
 * Both leg notionals per futures bar, one basket per session sized at the close of
 * the session before it.
 *
 * Stocks are forward-filled onto the futures bar grid, starting from their previous
 * close: a stock that has not traded yet today is still worth its close. The oldest
 * session fetched has no previous close in the window and is left out.
 */
export function buildNotionalSeries(
    futureCandles: Candle[],
    stockCandles: Record<string, Candle[]>,
    stocks: BasketStock[],
    multiplier: number,
): NotionalHistoryPoint[] {
    const points: NotionalHistoryPoint[] = [];

    for (const day of sessionDays(futureCandles)) {
        const marks = previousCloseMarks(futureCandles, stockCandles, stocks, day);
        const basket = spreadBasket(marks, stocks, multiplier);
        if (!basket) continue;

        const legs = stocks.map((stock) => ({
            candles: (stockCandles[stock.symbol] ?? []).filter((c) => vnDay(c.time) === day),
            cursor: 0,
            last: marks.byStock[stock.symbol] ?? null,
            quantity: basket[stock.symbol] ?? 0,
        }));

        for (const bar of futureCandles.filter((c) => vnDay(c.time) === day)) {
            const { futValue, equityValue } = computeLegNotional({
                futPrice: bar.close,
                multiplier,
                contracts: 1,
                stocks: legs.map((leg) => {
                    while (leg.cursor < leg.candles.length && leg.candles[leg.cursor].time <= bar.time) {
                        leg.last = leg.candles[leg.cursor].close;
                        leg.cursor += 1;
                    }
                    return { quantity: leg.quantity, price: leg.last, unit: STOCK_UNIT };
                }),
            });
            if (futValue != null && equityValue != null) {
                points.push({ time: bar.time, futValue, equityValue });
            }
        }
    }
    return points;
}
